import apiClient from './index';

export type Ticket = {
  id: string;
  number: number;
  title: string;
  description?: string | null;
  category?: string | null;
  priority: string;
  status: string;
  source?: string | null;
  apartment_id: string;
  resident_mobile_user_id?: string | null;
  created_by_user_id?: string | null;
  assignee_user_id?: string | null;
  due_at?: string | null;
  resolved_at?: string | null;
  closed_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TicketListResponse = {
  items: Ticket[];
  total: number;
};

export type TicketComment = {
  id: string;
  ticket_id: string;
  author_user_id?: string | null;
  author_mobile_user_id?: string | null;
  body: string;
  is_internal: boolean;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TicketHistoryItem = {
  id: string;
  ticket_id: string;
  from_status?: string | null;
  to_status: string;
  changed_by_user_id?: string | null;
  changed_by_mobile_user_id?: string | null;
  comment?: string | null;
  created_at?: string | null;
};

export type TicketAttachment = {
  id: string;
  ticket_id: string;
  comment_id?: string | null;
  uploaded_by_user_id?: string | null;
  uploaded_by_mobile_user_id?: string | null;
  storage_bucket: string;
  storage_key: string;
  file_name: string;
  content_type?: string | null;
  size_bytes?: number | null;
  created_at?: string | null;
};

export type TicketAttachmentPresignResponse = {
  upload_url: string;
  storage_bucket: string;
  storage_key: string;
  expires_in: number;
};

export type TicketAssigneeOption = {
  id: string;
  email: string;
  full_name?: string | null;
  role: string;
  kind: string;
};

export const ticketsApi = {
  async create(payload: {
    title: string;
    description?: string | null;
    category?: string | null;
    priority: string;
    status: string;
    source: string;
    apartment_id: string;
    assignee_user_id?: string | null;
    due_at?: string | null;
  }) {
    const res = await apiClient.post<Ticket>('/tickets/', payload);
    return res.data;
  },

  async list(params?: {
    status?: string;
    priority?: string;
    apartment_id?: string;
    assignee_user_id?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const res = await apiClient.get<TicketListResponse>('/tickets/', { params });
    return res.data;
  },

  async get(id: string) {
    const res = await apiClient.get<Ticket>(`/tickets/${id}`);
    return res.data;
  },

  async update(id: string, payload: Partial<Ticket>) {
    const res = await apiClient.patch<Ticket>(`/tickets/${id}`, payload);
    return res.data;
  },

  async listComments(id: string) {
    const res = await apiClient.get<TicketComment[]>(`/tickets/${id}/comments`);
    return res.data;
  },

  async createComment(id: string, payload: { body: string; is_internal?: boolean }) {
    const res = await apiClient.post<TicketComment>(`/tickets/${id}/comments`, payload);
    return res.data;
  },

  async listHistory(id: string) {
    const res = await apiClient.get<TicketHistoryItem[]>(`/tickets/${id}/history`);
    return res.data;
  },

  async listAttachments(id: string) {
    const res = await apiClient.get<TicketAttachment[]>(`/tickets/${id}/attachments`);
    return res.data;
  },

  async createAttachmentPresign(
    ticketId: string,
    payload: { file_name: string; content_type?: string | null }
  ) {
    const res = await apiClient.post<TicketAttachmentPresignResponse>(
      `/tickets/${ticketId}/attachments/presign`,
      payload
    );
    return res.data;
  },

  async createAttachment(
    ticketId: string,
    payload: {
      storage_bucket: string;
      storage_key: string;
      file_name: string;
      content_type?: string | null;
      size_bytes?: number | null;
      comment_id?: string | null;
    }
  ) {
    const res = await apiClient.post<TicketAttachment>(`/tickets/${ticketId}/attachments`, payload);
    return res.data;
  },

  async getAttachmentDownloadUrl(ticketId: string, attachmentId: string) {
    const res = await apiClient.get<{ download_url: string; expires_in: number }>(
      `/tickets/${ticketId}/attachments/${attachmentId}/download`
    );
    return res.data;
  },

  async listAssignees() {
    const res = await apiClient.get<TicketAssigneeOption[]>('/tickets/assignees');
    return res.data;
  },
};
